import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IOvertimeRegister } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { friendlyNote, money, MONTH_NAMES, monthYearLabel, triggerDownload } from './shared';
import { humanizeEnum } from '../../utils/format';

interface Props {
    institutionId: string;
}

const currentYear = () => new Date().getFullYear();
const ALL_MONTHS = '';
const EMPLOYEE_TOTALS_PAGE_SIZE = 10;

const OvertimeRegisterTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(currentYear());
    const [month, setMonth] = useState<number | ''>(ALL_MONTHS);
    const [register, setRegister] = useState<IOvertimeRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalsPage, setTotalsPage] = useState(0);

    const load = async () => {
        // M-3: a cleared/mid-edit year field parses to NaN — never fire the request with it.
        if (Number.isNaN(year)) return;
        setLoading(true);
        setPage(0);
        setTotalsPage(0);
        try {
            const data = await StatutoryRegisterService.getOvertimeRegister(
                institutionId, year, month === '' ? undefined : month,
            );
            setRegister(data);
            setLoadError(null);
        } catch (e) {
            // M-5: clear stale data on a failed reload so the table never shows a prior
            // period's figures under the newly-selected filter.
            setRegister(null);
            setLoadError('Could not load the overtime register.');
            toast.showApiError(e, 'Failed to load overtime register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        // H-2 principle applied here too: download the period the LOADED register represents,
        // never the live (possibly since-changed) filter state.
        if (!register) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadOvertimeRegisterXlsx(
                institutionId, register.year, register.month ?? undefined,
            );
            triggerDownload(blob, `overtime-register-${register.year}${register.month ? `-${register.month}` : ''}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!register) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadOvertimeRegisterPdf(
                institutionId, register.year, register.month ?? undefined,
            );
            triggerDownload(blob, `overtime-register-${register.year}${register.month ? `-${register.month}` : ''}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const pagedRows = register
        ? register.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];
    const pagedTotals = register
        ? register.employeeTotals.slice(
            totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE, totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE + EMPLOYEE_TOTALS_PAGE_SIZE,
        )
        : [];

    return (
        <Box data-help-page="statutory.overtime.page">
            <Box data-help-section="statutory.overtime.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    type="number"
                    data-help="statutory.overtime.year"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setYear(v);
                    }}
                    sx={{ flex: '1 1 120px', maxWidth: 192 }}
                />
                <TextField
                    select size="small" label="Month" value={month} data-help="statutory.overtime.month"
                    onChange={e => setMonth(e.target.value === '' ? '' : parseInt(e.target.value as string, 10))}
                    sx={{ flex: '1 1 150px', maxWidth: 240 }}
                >
                    <MenuItem value="">All months</MenuItem>
                    {MONTH_NAMES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <Button data-help="statutory.overtime.load" variant="outlined" size="small" onClick={load} disabled={loading}>Load</Button>
                <Button data-help="statutory.overtime.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.overtime.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && loadError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="overtime-load-error">{loadError}</Alert>
            )}

            {!loading && register && (
                <>
                    {register.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }} data-help="statutory.overtime.caveat">{friendlyNote(c)}</Alert>
                    ))}

                    <TableContainer component={Paper} data-testid="overtime-register-table" data-help-section="statutory.overtime.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.overtime.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.overtime.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.overtime.col.month">Month</TableCell>
                                    <TableCell align="right" data-help="statutory.overtime.col.shiftMinutes">Overtime Minutes (shift, authoritative)</TableCell>
                                    {/* Label matches the XLSX sheet header exactly (M-4). */}
                                    <TableCell align="right" data-help="statutory.overtime.col.hours">Payable Overtime Hours (after per-shift threshold and cap; rounded)</TableCell>
                                    <TableCell align="right" data-help="statutory.overtime.col.payrollMinutes">Payroll Minutes</TableCell>
                                    <TableCell align="right" data-help="statutory.overtime.col.amount">Amount</TableCell>
                                    <TableCell data-help="statutory.overtime.col.paid">Paid</TableCell>
                                    <TableCell data-help="statutory.overtime.col.status">Status</TableCell>
                                    <TableCell align="right" data-help="statutory.overtime.col.rate">Rate (indicative)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <Typography color="text.secondary">No rows for this period.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map((row, idx) => (
                                    <TableRow key={`${row.userId}-${row.month}-${idx}`} hover>
                                        <TableCell>{row.employeeName ?? '—'}</TableCell>
                                        <TableCell>{row.employeeCode ?? '—'}</TableCell>
                                        <TableCell>{monthYearLabel(register.year, row.month)}</TableCell>
                                        <TableCell align="right">{row.overtimeMinutes}</TableCell>
                                        <TableCell align="right">{row.overtimeHours.toFixed(2)}</TableCell>
                                        <TableCell align="right">
                                            {row.minutesMismatch
                                                ? <span data-testid="ot-minutes-mismatch">
                                                    shift {row.overtimeMinutes} / payroll {row.payrollMinutes}
                                                  </span>
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.payrollStatus == null
                                                ? <Typography variant="body2" color="text.secondary" component="span">Not in payroll yet</Typography>
                                                : money(row.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.paidThroughPayroll ? 'Yes' : 'No'}
                                                color={row.paidThroughPayroll ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>{row.payrollStatus ? humanizeEnum(row.payrollStatus) : '—'}</TableCell>
                                        <TableCell align="right" data-testid={`ot-rate-${row.userId}-${row.month}`}>
                                            {row.effectiveHourlyRate === null ? '—' : money(row.effectiveHourlyRate)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                {/* M-6: only the Grand Total renders in the always-visible footer. */}
                                <TableRow data-help="statutory.overtime.grandTotal" sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                                    <TableCell colSpan={3}>Grand Total</TableCell>
                                    <TableCell align="right">{register.grandTotal.totalMinutes}</TableCell>
                                    <TableCell colSpan={3} />
                                    <TableCell align="right">{money(register.grandTotal.totalAmount)}</TableCell>
                                    <TableCell colSpan={2} />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={register.rows.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="overtime-employee-totals-section" data-help-section="statutory.overtime.employeeTotals">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Employee Totals ({register.employeeTotals.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.overtime.totals.col.employee">Employee</TableCell>
                                            <TableCell align="right" data-help="statutory.overtime.totals.col.minutes">Total Minutes</TableCell>
                                            <TableCell align="right" data-help="statutory.overtime.totals.col.amount">Total Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedTotals.map(t => (
                                            <TableRow key={t.userId}>
                                                <TableCell>{t.employeeName ?? t.userId}</TableCell>
                                                <TableCell align="right">{t.totalMinutes}</TableCell>
                                                <TableCell align="right">{money(t.totalAmount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={register.employeeTotals.length}
                                page={totalsPage}
                                onPageChange={(_, p) => setTotalsPage(p)}
                                rowsPerPage={EMPLOYEE_TOTALS_PAGE_SIZE}
                                rowsPerPageOptions={[EMPLOYEE_TOTALS_PAGE_SIZE]}
                            />
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default OvertimeRegisterTab;
